<template>
    <div :id="tableId" class="snappy-table">
        <v-server-table
                :url="endpoint"
                :columns="columns"
                :options="options"
                :no-results-message="noResultsMessage"
                :class="[tableClass]"
                @loaded="onLoaded"
                @loading="onLoading"
                @row-click="onRowClick"
                ref="table"
                v-cloak
        >
            <div v-if="loading && loadingBackdropStyle" slot="beforeBody" class="table-loading-wrapper">
                <div class="table-loading-backdrop" :style="loadingBackdropStyle">
                    <div class="table-loading-indicator"></div>
                </div>
            </div>
            <template v-for="name in templatedSlotNames" :slot="name" slot-scope="props">
                <slot :name="name" :row="props.row" />
            </template>
        </v-server-table>
    </div>
</template>

<script>
import Vue from 'vue';
import {ServerTable} from 'vue-tables-2';
import axios from 'axios';
import * as Sentry from "@sentry/vue";

Vue.use(ServerTable);

    export default {
        name: 'snappy-table',
        components: {ServerTable},
        data() {
            let self = this;
            return {
                loading: false,
                loadingBackdropStyle: null,
                options: {
                    requestFunction(params) {
                        self.params = self.queryAdapter(params);
                        return self.httpRequest().catch(error => {
                            if (error.response) {
                                self.onHttpErrorResponse(error.response);
                            } else {
                              Sentry.captureException(error);
                            }
                        });
                    },
                    responseAdapter(response) {
                        const {data, meta} = (response && response.data) || {};

                        return {
                            data: data || [],
                            count: (meta && meta.total_count) || 0
                        };
                    },
                    texts: {
                        filterPlaceholder:'Search',
                        count: `{count} ${this.recordType}`,
                        filter:'',
                        limit:"Items Per Page:",
                        noResults: this.noResultsMessage && this.noResultsMessage.length > 0 ? this.noResultsMessage : `No ${this.recordType} Found`
                    },
                    columnsClasses: this.setColumnClasses(),
                    templates: this.templateMethods,
                    debounce: 2000,
                    dateFormat: 'MM/DD/YYYY',
                    sortable: ['created_at','launch'],
                    dateColumns:['initiated_at', 'finished_at', 'created_at','launch'],
                    headings: this.getHeadings(),
                    filterByColumn: this.filterableColumns.length > 0,
                    filterable: this.isFilterable(),
                    listColumns: this.filterValues,
                    orderBy: this.defaultOrdering,
                    rowClassCallback: this.rowClassCallback,
                    hiddenColumns: this.hiddenColumns && this.hiddenColumns.length > 0 ? this.hiddenColumns : false
                },
                params: {},
                tableId: `snappy-table-${Math.random().toString(36).slice(2)}`
            };
        },
        methods: {
            async httpRequest() {
                let url = '/api/?entity=vessel&action=queryTable';
                url = this.endpointExcludeFleet ? url += '&excludeFleet=1' : url;

                return axios.post(
                    url,
                    {
                        url: this.requestUrl
                    }
                );
            },
            onHttpErrorResponse(response) {
                $.notify('Failed to get data.', 'error');
            },
            onRowClick(event){
                this.handleRowClick(event);
            },
            getHeadings() {
                let headings = {};
                this.columns.map((key, i) => headings[key] = this.headings[i]);
                return headings;
            },
            setColumnClasses() {
                let classes = {};
                this.columns.map((key) => classes[key] = key);
                return classes;
            },
            queryAdapter(params) {
                let orderDirection = params.ascending !== 1 ? 'asc' : 'desc';
                let query = {limit:params.limit, page:params.page, order_direction:orderDirection};
                let keys = Object.keys(params.query);

                if (!!params.orderBy) {
                  query['order_by'] = params.orderBy;
                } else if (this.searchOrderBy) {
                  query['order_by'] = this.searchOrderBy;
                } else {
                  query['order_by'] = 'created_at';
                }

                keys.forEach( (filter) => {
                    if(filter === 'status' || filter === 'groupcontactselected' || filter === 'contacteditgroupmembership') {
                        query['status'] = params.query[filter];
                    } else if(filter === 'email' || filter === 'first_name' || filter === 'last_name' || filter === 'name' || filter === 'contactgroup') {
                        query['query'] = params.query[filter];
                    } else if(filter === 'type') {
                        const typeParam = (this.filterParamsMapper || {}).type || 'activity_type_id';
                        query[typeParam] = params.query[filter];
                    } else if(filter === 'contact') {
                        query[filter] = params.query[filter];
                    } else if(filter === 'campaign') {
                        query['campaign_id'] = params.query[filter];
                    } else if(filter === 'created_at') {
                        let startDate = new Date(params.query[filter].start);
                        startDate = startDate.getFullYear() + '-' + (startDate.getMonth() + 1) + '-' + startDate.getDate();
                        query['start_date'] = startDate;

                        let endDate = new Date(params.query[filter].end);
                        endDate = endDate.getFullYear() + '-' + (endDate.getMonth() + 1) + '-' + endDate.getDate();
                        query['end_date'] = endDate;
                    } else if(filter === 'global_unsubscribe') {
                        query[filter] = params.query[filter];
                    } else {
                        query['query'] = params.query;
                    }
                });

                Object.entries(this.defaultParams || {}).forEach(([key, value]) => {
                    if (key === 'activity_type_ids[]') {
                        if (!query['activity_type_ids[]']) {
                            value.forEach((value, index) => {
                                query['activity_type_ids[' + index + ']'] = value;
                            });
                        }
                    } else {
                        query[key] = value;
                    }
                });

                return query;
            },
            onLoading() {
                this.loading = true;
                this.recalculateLoadingBackdropDimensions();
            },
            onLoaded() {
                this.loading = false;
            },
            recalculateLoadingBackdropDimensions() {
                const tbody = document.querySelector(`#${this.tableId} tbody`);
                if (tbody) {
                    this.loadingBackdropStyle = {
                        height: `${tbody.clientHeight}px`,
                        width: `${tbody.clientWidth}px`
                    };
                }
            },
            refresh() {
                this.$refs.table.refresh();
            },
            isFilterable() {
                if (this.searchHidden) {
                    return false;
                }
                return this.filterableColumns.length > 0 ? this.filterableColumns : this.columns;
            }
        },
        computed: {
            requestUrl: function() {
                const symbol = this.endpoint.includes('?') ? '&' : '?';
                return this.endpoint + symbol + this.query;
            },
            query: function() {
                let keys = Object.keys(this.params);
                let str = '';
                keys.forEach( (filter,index) => {
                    str += filter + '=' + this.params[filter];

                    if(index+1 !== keys.length) {
                        str += "&";
                    }
                });

                return str;
            }
        },
        watch: {
            clickToToggleParams: {
                handler(meta){
                    if(meta.toggle) {
                        this.$refs.table.setFilter(meta.params);
                    } else {
                        this.$refs.table.resetQuery();
                    }
                },
                deep: true
            }
        },
        props: {
            endpoint: {
                type: String,
                required: true
            },
            endpointExcludeFleet: {
              type: Boolean,
              required: false,
            },
            searchOrderBy: {
              type: String,
              required: false,
            },
            columns: {
                type: Array,
                required: true
            },
            templateMethods: {
                type: Object,
                required: false
            },
            handleRowClick: {
                type: Function,
                required: false,
                default: () => {
                    return false;
                }
            },
            templatedSlotNames: {
                type: Array,
                required: false,
                default: () => []
            },
            headings: {
                type: Array,
                required: true
            },
            tableClass: {
                type: String,
                required: false,
                default: ''
            },
            filterValues: {
                type: Object,
                required: false,
                default: () => {}
            },
            filterableColumns: {
                type: Array,
                required: false,
                default: () => []
            },
            clickToToggleParams: {
                type: Object,
                required: false,
                default: () => {}
            },
            defaultParams: {
                type: Object,
                required: false,
                default: () => {}
            },
            defaultOrdering: {
                type: Object,
                required: false,
                default: () => {}
            },
            recordType: {
                type: String,
                required: false,
                default: 'Records'
            },
            rowClassCallback: {
                type: Function,
                required: false,
            },
            hiddenColumns: {
                type: Array,
                required: false,
            },
            searchHidden: {
                type: Boolean,
                required: false,
            },
            noResultsMessage: {
                type: String,
                required: false,
            },
            filterParamsMapper: {
                type: Object,
                required: false,
            }
        }
    }
</script>

<style lang="scss" scoped>
    .table-loading-wrapper {
        position: relative;
    }

    .table-loading-backdrop {
        background: rgba(#ffffff, 0.75);
        display: flex;
        align-items: center;
        justify-content: center;
        position: absolute;
        top: 0;
    }

    .table-loading-indicator {
        background: center / 50px no-repeat url("/wp-content/themes/snappy-theme/assets/images/loading2.gif");
        width: 50px;
        height: 50px;
    }
</style>
